import { APR_LABELS } from '@pickleballinc/lib/product-labels';
import { useQuery } from '@tanstack/react-query';
import { useTransition } from 'react';
import { toast } from 'react-toastify';

import { APR_PAGE_SIZE } from '@/modules/apr/rankings/constants';
import {
  FormattedAPRRankingProfileData,
  BracketStrengthData,
  PlayerProfileData,
  PlayerProfileGraphData,
  TournamentEventsData,
  TournamentsResultsData,
  TournamentStrengthCamel,
} from '@/types/aprRankings.types';
import { downloadCsv } from '@/utils/helpers/globalRankingsUtils';
import { DefaultMiddlewareRes } from '@/utils/helpers/middlewareTypes';

async function getProfileData(
  slug: string,
  divisionType: string | null
): Promise<
  | {
      playerProfileData: PlayerProfileData;
    }
  | undefined
> {
  try {
    const response = await fetch(
      `/api/v2/apr/getPlayerProfileData?slug=${slug}&divisionType=${divisionType}`
    );

    if (response.ok) {
      const data: { data: { playerProfileData: PlayerProfileData } } = await response.json();
      return data.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return undefined;
  }
}

async function getAPRRankingProfileData(
  slug: string
): Promise<{ data: FormattedAPRRankingProfileData | undefined; error?: string }> {
  try {
    const response = await fetch(`/api/v2/apr/getRankingProfileData?slug=${slug}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }
    const data: DefaultMiddlewareRes<FormattedAPRRankingProfileData> = await response.json();
    return { data: data.data };
  } catch {
    return { data: undefined, error: `Failed to fetch ${APR_LABELS.long} for selected player!` };
  }
}

async function getProfileGraphData(slug: string): Promise<
  | {
      playerGraphData: PlayerProfileGraphData;
    }
  | undefined
> {
  try {
    const response = await fetch(`/api/v2/apr/getPlayerProfileGraphData?slug=${slug}`);

    if (response.ok) {
      const data: { data: { playerGraphData: PlayerProfileGraphData } } = await response.json();
      return data.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return undefined;
  }
}

async function getRankHistoryData(
  slug: string,
  divisionType: string,
  rankBy: string,
  raceFilterYear: number,
  page: number
): Promise<
  | {
      rankHistoryData: TournamentsResultsData[];
      totalRecords: number;
    }
  | undefined
> {
  try {
    const response = await fetch(
      `/api/v2/apr/rankHistoryData?slug=${slug}&divisionType=${divisionType}&rankBy=${rankBy}&raceFilterYear=${raceFilterYear}&page=${page}`
    );

    if (response.ok) {
      const data: {
        data: { totalRecords: number; rankHistoryData: TournamentsResultsData[] };
      } = await response.json();
      return data.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return undefined;
  }
}

async function getTournamentStrength(
  uuid: string,
  tournamentUuid: string
): Promise<
  | {
      tournamentStrengthData: TournamentStrengthCamel;
    }
  | undefined
> {
  try {
    const response = await fetch(
      `/api/v2/apr/getTournamentStrength?uuid=${uuid}&tournamentUuid=${tournamentUuid}`
    );

    if (response.ok) {
      const data: { data: { tournamentStrengthData: TournamentStrengthCamel } } =
        await response.json();
      return data.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return undefined;
  }
}

async function getBracketStrength(
  uuid: string,
  bracketUuid: string
): Promise<
  | {
      bracketStrengthData: BracketStrengthData;
    }
  | undefined
> {
  try {
    const response = await fetch(
      `/api/v2/apr/getBracketStrength?uuid=${uuid}&bracketUuid=${bracketUuid}`
    );

    if (response.ok) {
      const data: { data: { bracketStrengthData: BracketStrengthData } } = await response.json();
      return data.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return undefined;
  }
}

async function getTournamentEventsData(
  tournamentId: string,
  eventFormat: string,
  pageSize?: string,
  currentPage?: string
): Promise<TournamentEventsData> {
  const baseUrl = '/api/v2/apr/getTournamentEventsData';
  const searchParams = new URLSearchParams({
    tournamentId: tournamentId,
    eventFormat: eventFormat,
    pageSize: pageSize ?? APR_PAGE_SIZE,
    currentPage: currentPage ?? '1',
  });
  try {
    const response = await fetch(`${baseUrl}?${searchParams.toString()}`);
    if (response.ok) {
      const result: { data: TournamentEventsData } = await response.json();
      return result.data;
    } else {
      throw new Error('Failed to fetch data');
    }
  } catch {
    return { data: [], totalRecords: 0 };
  }
}

export const useGetProfileData = (
  slug: string,
  divisionType: string | null,
  worldRanking: boolean,
  mockData?: PlayerProfileData
) => {
  return useQuery({
    queryKey: ['apr_profile_data', slug, divisionType],
    queryFn: () =>
      mockData
        ? Promise.resolve({ playerProfileData: mockData })
        : getProfileData(slug, divisionType),
    enabled: !!slug && !!divisionType && !worldRanking,
    refetchOnWindowFocus: false,
  });
};

export const useGetAPRRankingProfileData = (slug: string, enabled = true) => {
  return useQuery({
    queryKey: ['apr_ranking_profile_data', slug, enabled],
    queryFn: () => getAPRRankingProfileData(slug),
    enabled: !!slug && enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetProfileGraphData = (
  slug: string,
  worldRanking: boolean,
  mockData?: PlayerProfileGraphData
) => {
  return useQuery({
    queryKey: ['apr_graph_data', slug],
    queryFn: () =>
      mockData ? Promise.resolve({ playerGraphData: mockData }) : getProfileGraphData(slug),
    enabled: !!slug && !worldRanking,
    refetchOnWindowFocus: false,
  });
};

export const useGetRankHistory = (
  slug: string,
  divisionType: string,
  rankBy: string,
  raceFilterYear: number,
  page: number
) => {
  return useQuery({
    queryKey: ['apr_rank_history_data', slug, divisionType, rankBy, raceFilterYear],
    queryFn: () => getRankHistoryData(slug, divisionType, rankBy, raceFilterYear, page),
    enabled: slug !== '' && divisionType !== '',
    refetchOnWindowFocus: false,
  });
};

export const useGetRaceYearOptions = (slug: string, divisionType: string) => {
  return useQuery({
    queryKey: ['apr_race_year_data', slug, divisionType],
    queryFn: () => getRankHistoryData(slug, divisionType, 'race', new Date().getFullYear(), 1),
    enabled: slug !== '' && divisionType !== '',
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useGetTournamentStrength = (uuid: string, tournamentUuid: string) => {
  return useQuery({
    queryKey: ['apr_tournament_strength', uuid, tournamentUuid],
    queryFn: () => getTournamentStrength(uuid, tournamentUuid),
    enabled: uuid !== '' && tournamentUuid !== '',
    refetchOnWindowFocus: false,
  });
};

export const useGetBracketStrength = (uuid: string, bracketUuid: string) => {
  return useQuery({
    queryKey: ['apr_bracket_strength', uuid, bracketUuid],
    queryFn: () => getBracketStrength(uuid, bracketUuid),
    enabled: uuid !== '' && bracketUuid !== '',
    refetchOnWindowFocus: false,
  });
};

export const useGetTournamentEventsData = (params: {
  tournamentId: string;
  eventFormat: string;
  pageSize?: string;
  currentPage?: string;
}) => {
  const { tournamentId, eventFormat, pageSize, currentPage } = params;
  return useQuery({
    queryKey: ['apr_tournament_profile_data', tournamentId, eventFormat, pageSize, currentPage],
    queryFn: () => getTournamentEventsData(tournamentId, eventFormat, pageSize, currentPage),
    enabled: !!tournamentId && !!eventFormat,
    refetchOnWindowFocus: false,
  });
};

type ExportRankingsFn<TParams> = (params: TParams) => Promise<{
  data: string;
  statusCode: number;
}>;

interface UseExportRankingsParams<TParams> {
  exportFn: ExportRankingsFn<TParams>;
  getFileName: (params: TParams) => string;
}

export const useExportRankings = <TParams>({
  exportFn,
  getFileName,
}: UseExportRankingsParams<TParams>) => {
  const [isExporting, startTransition] = useTransition();

  const exportCsv = (params: TParams) => {
    startTransition(async () => {
      try {
        const res = await exportFn(params);

        if (!res?.data) {
          throw new Error('No CSV data returned');
        }

        const fileName = getFileName(params);

        downloadCsv(res.data, fileName);
      } catch {
        toast.error('CSV export failed. Please try again later.');
      }
    });
  };

  return {
    exportCsv,
    isExporting,
  };
};
